
import React from 'react';

interface AIImageProps {
  prompt?: string;
  src?: string;
  alt: string;
  className?: string;
  aspectRatio?: "1:1" | "3:4" | "4:3" | "9:16" | "16:9";
  sizes?: string;
  fit?: "cover" | "contain";
  loading?: "lazy" | "eager";
  width?: number;
  height?: number;
  fetchPriority?: "high" | "low" | "auto";
  decoding?: "async" | "sync" | "auto";
}

const DEFAULT_WIDTHS = [640, 960, 1280, 1600];
const ASPECT_DIMENSIONS: Record<NonNullable<AIImageProps["aspectRatio"]>, { width: number; height: number }> = {
  "1:1": { width: 1200, height: 1200 },
  "3:4": { width: 1200, height: 1600 },
  "4:3": { width: 1600, height: 1200 },
  "9:16": { width: 900, height: 1600 },
  "16:9": { width: 1600, height: 900 },
};

const AIImage: React.FC<AIImageProps> = ({
  prompt,
  src,
  alt,
  className = "",
  aspectRatio = "16:9",
  sizes = "100vw",
  fit = "cover",
  loading = "lazy",
  width,
  height,
  fetchPriority = "auto",
  decoding = "async",
}) => {
  const baseUrl = src
    ? `/image?src=${encodeURIComponent(src)}`
    : `/ai-image?prompt=${encodeURIComponent(prompt || "")}&aspect=${encodeURIComponent(aspectRatio)}`;
  const buildUrl = (width: number) => `${baseUrl}&w=${width}`;
  const srcSet = DEFAULT_WIDTHS.map((width) => `${buildUrl(width)} ${width}w`).join(', ');
  const imageUrl = buildUrl(DEFAULT_WIDTHS[1]);
  const fitClass = fit === "contain" ? "object-contain" : "object-cover";
  const resolvedDimensions = ASPECT_DIMENSIONS[aspectRatio];
  const resolvedWidth = width ?? resolvedDimensions.width;
  const resolvedHeight = height ?? resolvedDimensions.height;

  return (
    <img
      src={imageUrl}
      srcSet={srcSet}
      sizes={sizes}
      alt={alt}
      className={`${fitClass} rounded-[2rem] transition-opacity duration-1000 ${className}`}
      loading={loading}
      width={resolvedWidth}
      height={resolvedHeight}
      fetchPriority={fetchPriority}
      decoding={decoding}
    />
  );
};

export default AIImage;
